import { LitElement, html, css } from 'lit';
import { UrlConverter } from '../lit/converters/UrlConverter';
import '../cbom-configurator/CbomConfigurator';
import '../cbom-product/CbomProduct';
import '../cbom-category/CbomCategory';
import '../cbom-mobile-cta/CbomMobileCta';
import '../cbom-product-detail-card/CbomProductDetailCard';

class CbomApp extends LitElement {
  // Properties
  static properties = {
    id: { type: Number },
    action: { type: URL, converter: UrlConverter },
    title: { type: String },
    data: { type: Object, state: true },
    open: { type: Boolean },
    isMobile: { type: Boolean },
    hoveredProductInfo: { type: Object },
    translations: { type: Object },
    categories: { type: Object },
  };

  static styles = css`
    :host {
      position: relative;
    }
    :host([ismobile]) {
      padding: 10px;
      display: block;
    }
  `;

  constructor() {
    super();

    // Configurator top
    this.configuratorOffsetTop = 0;
  }

  // Get the current product selections ids
  _getSelections() {
    let selections = [];
    this.categories.forEach((category) => {
      selections.push(
        category.arr_products.find((product) => product.bol_current).int_id
      );
    });
    return selections;
  }

  // Passes current bundle config info
  // Uses response to update components
  updateData() {
    // updateData is called twice initially, the second time categories is filled
    if (!this.categories) {
      return;
    }

    const configurator = this.shadowRoot.querySelector('cbom-configurator');
    const data = {
      int_cbom_id: 0,
      arr_selections: {},
    };

    // Spinner
    configurator.feedback = null;
    configurator.isLoading = true;

    // Get the configuration structure
    this.categories.forEach((category) => {
      data.arr_selections[category.int_id] = category.arr_products.find(
        (product) => product.bol_current
      ).int_id;
    });

    // Append request data
    data.int_cbom_id = this.id;

    // Send payload to backend using fetch
    fetch(this.action, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        // Hide the loader
        configurator.isLoading = false;
        return response;
      })
      .then((response) => {
        if (!response?.ok) {
          throw new Error('Response not OK');
        }
        return response;
      })
      .then((response) => response.json())
      .then((response) => {
        // Emit event to then update price component
        // `composed` and `bubbles` allow the event to emit outside of the component
        this.dispatchEvent(
          new CustomEvent('cbomproductupdate', {
            detail: {
              price: response.price,
              priceNoFormat: response.price_no_format,
              warrantyPrice: response.flt_warranty_price,
              warrantyPriceNoFormat: response.flt_warranty_price_no_format,
              preDiscountedPrice: response.preDiscountedPrice,
              stock: response.bol_is_in_stock,
              stockMessage: response.str_stock_message,
              deliveryExpectation: response.str_delivery_advice,
              selections: this._getSelections(),
              selectionsKeyed: data.arr_selections,
            },
            composed: true,
            bubbles: true,
          })
        );
      })
      .catch(() => {
        configurator.feedback =
          'Server error while submitting selection, please try again';
      });
  }

  render() {
    return html`
      ${this.renderMobileCta()} ${this.renderProductDetailCard()}
      <cbom-configurator
        name="${this.title}"
        ?open=${this.open}
        ?isMobile=${this.isMobile}
      >
        ${this.categories.map((category) => this.renderCategory(category))}
      </cbom-configurator>
    `;
  }

  // Get actual offset from top of the page by recursing through parents
  getOffsetTop(element) {
    return element
      ? element.offsetTop + this.getOffsetTop(element.offsetParent)
      : 0;
  }

  // Once configurator is rendered grab the configurators offset top
  updated() {
    const configurator = this.shadowRoot.querySelector('cbom-configurator');
    this.configuratorOffsetTop = this.getOffsetTop(configurator);
  }

  // Render out the product category
  renderCategory({ int_id, str_name, arr_products, open }) {
    let currentProduct = arr_products.find((product) => product.bol_current);

    return html`
      <cbom-category
        .id=${int_id}
        name="${str_name}"
        ?isMobile=${this.isMobile}
        ?open=${open}
        @cbomcategoryclick=${this._categoryClick}
        title=${(open
          ? this.translations.category.title.close
          : this.translations.category.title.open) +
        ' ' +
        str_name}
      >
        ${this.renderProduct(
          {
            id: currentProduct.int_id,
            name: currentProduct.str_name,
            src: currentProduct.str_image_small,
            price: currentProduct.str_price,
            current: currentProduct.bol_current,
          },
          { int_id, str_name }, // Category
          true
        )}
        ${arr_products.map((product) =>
          this.renderProduct(
            {
              id: product.int_id,
              name: product.str_name,
              src: product.str_image_small,
              price: product.str_price,
              current: product.bol_current,
            },
            { int_id, str_name }
          )
        )}
      </cbom-category>
    `;
  }

  // Render the product
  renderProduct({ id, name, src, price, current }, category, parent) {
    return html`
      <cbom-product
        @cbomproductclick=${this._productClick}
        @cbomproductmouseenter=${this._showProductCard}
        @cbomproductmouseleave=${this._closeProductDetailCard}
        id="${id}"
        .category="${category}"
        name="${name}"
        ?current=${current}
        src="${src}"
        price="${parent ? null : price}"
        ?selected=${current && !parent}
        ?parent=${parent}
        slot="${parent ? 'current' : null}"
        ?isMobile=${this.isMobile}
        title=${current
          ? ''
          : `${this.translations.product.title.select} ${name}`}
      ></cbom-product>
    `;
  }

  // Render the mobile CTA if required
  renderMobileCta() {
    if (this.isMobile) {
      return html`
        <cbom-mobile-cta
          @cbommobilectaclick=${this._mobileCtaClick}
          .open=${this.open}
          name="${this.title}"
        >
        </cbom-mobile-cta>
      `;
    }
  }

  // Event handler for when the mobile product detail card 'Select Product' is hit
  _onProductDetailCardSelectProductClick(event) {
    this._selectProduct(event);
    this._closeProductDetailCard();
  }

  // Renders out the product detail card
  renderProductDetailCard() {
    if (this.hoveredProductInfo) {
      return html`
        <cbom-product-detail-card
          ?isMobile=${this.isMobile}
          .title=${this.hoveredProductInfo.data.str_name}
          .src=${this.hoveredProductInfo.data.str_image_large}
          .price=${this.hoveredProductInfo.data.str_price}
          .keyFeatures=${this.hoveredProductInfo.data.str_key_features}
          .shortDescription=${this.hoveredProductInfo.data
            .str_short_description}
          .id=${this.hoveredProductInfo.data.int_id}
          .category=${this.hoveredProductInfo.data.category}
          .url=${this.hoveredProductInfo.data.str_url}
          .productOffsetTop=${this.hoveredProductInfo.productOffsetTop}
          .configuratorOffsetTop=${this.configuratorOffsetTop}
          .translations=${this.translations.productDetailCard}
          @cbomproductdetailcardselectproductclick=${this
            ._onProductDetailCardSelectProductClick}
          @cbomproductdetailcardcloseclick=${this._closeProductDetailCard}
          @cbomproductcardmouseleave=${this._closeProductDetailCard}
        >
        </cbom-product-detail-card>
      `;
    }
  }

  // Handle the product click event
  _productClick(event) {
    if (this.isMobile) {
      this._showProductCard(event);
    } else {
      this._selectProduct(event);
      this._closeProductDetailCard();
    }
  }

  // Handle the category click event
  _categoryClick(event) {
    // Close all other categories
    this._closeAllCategories();

    // Set the open category as the chosen one
    this.categories.map((category) => {
      if (category.int_id == event.detail.id && event.detail.open) {
        category.open = true;
      }
    });

    this.requestUpdate();
  }

  _closeAllCategories() {
    this.categories = this.categories.map((category) => {
      category.open = false;
      return category;
    });
  }

  // Handle the select product event
  _selectProduct(event) {
    // Get the current category from the event
    const currentCategory = this.categories.find(
      (category) => category.int_id == event.detail.category.int_id
    );

    // Set the next category which will open after this selection has been made
    // Closes all other categories
    let nextIndex;
    this.categories.map((category, index) => {
      if (
        category.int_id === currentCategory.int_id &&
        index + 1 in this.categories
      ) {
        nextIndex = index + 1;
      }

      category.open = index === nextIndex;
      return category;
    });

    // Set the current property on all products based on the id from the event
    currentCategory.arr_products.map((product) => {
      product.bol_current = product.int_id == event.detail.id;
      return product;
    });

    // Update
    this.updateData();

    // Rerender the relevant components to reflect the property changes
    this.requestUpdate();
  }

  // Only re-render the product card if the ID from the event differs from the ID of the card that is already rendered
  _showProductCard(event) {
    if (this?.hoveredProductInfo?.data?.int_id !== event.detail.id) {
      const currentCategory = this.categories.find(
        (category) => category.int_id == event.detail.category.int_id
      );
      const currentProduct = currentCategory.arr_products.find(
        (product) => product.int_id == event.detail.id
      );
      if (!currentProduct) {
        return this._productClick(event);
      }
      currentProduct.category = currentCategory;

      this.hoveredProductInfo = {
        data: currentProduct,
        productOffsetTop: this.getOffsetTop(event.detail.productElement),
      };
    }
  }

  // Closes down the product detail card
  _closeProductDetailCard() {
    this.hoveredProductInfo = undefined;
  }

  // Handle the mobile CTA click event and open
  _mobileCtaClick(event) {
    this.open = event.detail.open;
  }
}

customElements.define('cbom-app', CbomApp);
